import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { GitCompare, Star, X, Check, Minus } from "lucide-react";

export default function Compare() {
  const { compare, toggleCompare, format } = useApp();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (compare.length === 0) { setProducts([]); return; }
    api.get("/products", { params: { limit: 200 } }).then((r) => {
      setProducts(r.data.filter((p) => compare.includes(p.id)));
    });
  }, [compare]);

  if (products.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <GitCompare className="w-10 h-10 mx-auto mb-4 text-primary" />
        <h1 className="text-3xl font-display font-bold mb-3">Nada para comparar</h1>
        <p className="text-muted-foreground mb-6">Adiciona 2 a 4 produtos usando o botão "Comparar" nas fichas de produto.</p>
        <Button asChild size="lg" className="rounded-full btn-primary"><Link to="/produtos">Ver produtos</Link></Button>
      </div>
    );
  }

  const Row = ({ label, render }) => (
    <tr className="border-t border-border">
      <td className="p-4 text-xs uppercase tracking-widest text-muted-foreground w-40 align-top">{label}</td>
      {products.map((p) => (
        <td key={p.id} className="p-4 text-sm align-top">{render(p)}</td>
      ))}
    </tr>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <div className="flex items-center gap-2 mb-8">
        <GitCompare className="w-6 h-6 text-primary" />
        <h1 className="text-4xl font-display font-bold">Comparador</h1>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full">
          <thead>
            <tr>
              <th className="p-4 text-left w-40"></th>
              {products.map((p) => (
                <th key={p.id} className="p-4 text-left min-w-[220px]" data-testid={`compare-col-${p.slug}`}>
                  <div className="relative">
                    <button onClick={() => toggleCompare(p.id)} className="absolute top-0 right-0 p-1 rounded-full hover:bg-muted" data-testid={`compare-remove-${p.slug}`}>
                      <X className="w-4 h-4" />
                    </button>
                    <img src={p.image} alt={p.name} className="w-full h-40 object-cover rounded-xl mb-3" />
                    <Link to={`/produto/${p.slug}`} className="font-display font-semibold hover:text-primary">{p.name}</Link>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="font-display font-bold text-lg">{format(p.sale_price_eur ?? p.price_eur)}</span>
                      {p.sale_price_eur && <span className="text-xs line-through text-muted-foreground">{format(p.price_eur)}</span>}
                    </div>
                    <Button asChild size="sm" className="rounded-full btn-primary mt-3 w-full">
                      <Link to={`/produto/${p.slug}`}>Ver produto</Link>
                    </Button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label="Categoria" render={(p) => p.category_slug} />
            <Row label="Avaliação" render={(p) => (
              <div className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /><span>{p.rating}</span><span className="text-muted-foreground text-xs">({p.reviews_count})</span></div>
            )} />
            <Row label="Preço base" render={(p) => format(p.price_eur)} />
            <Row label="Preço promo" render={(p) => p.sale_price_eur ? <span className="text-primary font-semibold">{format(p.sale_price_eur)}</span> : <Minus className="w-3.5 h-3.5 text-muted-foreground" />} />
            <Row label="Stock" render={(p) => p.stock > 0 ? <span className="text-green-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {p.stock} un.</span> : <span className="text-destructive">Esgotado</span>} />
            <Row label="Cores" render={(p) => p.colors?.length ? p.colors.join(", ") : <Minus className="w-3.5 h-3.5 text-muted-foreground" />} />
            <Row label="Modelos" render={(p) => p.sizes?.length ? p.sizes.join(", ") : <Minus className="w-3.5 h-3.5 text-muted-foreground" />} />
            <Row label="Peso" render={(p) => `${p.weight_g} g`} />
            <Row label="Descrição" render={(p) => <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">{p.description}</p>} />
          </tbody>
        </table>
      </div>
    </div>
  );
}
